import type { Lot, State } from '../types';
import { apiUrl, log, roundToTwo } from './common';

export async function syncEstimateRemote(
  id: string,
  lowOverride: any,
  highOverride: any,
  retry: boolean,
  state: State,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>,
  mapRemoteDocument: (data: any) => Lot | null | undefined
): Promise<Lot | null> {
  let token: string | null;
  try {
    token = await ensureAccessToken(Boolean(retry));
  } catch {
    throw new Error('Unable to acquire access token.');
  }
  if (!token) throw new Error('Unable to acquire access token.');
  const endpoint = apiUrl('extensions/scrapes/' + encodeURIComponent(id));
  const updatesPayload: any = {};
  if (lowOverride !== undefined) {
    updatesPayload.lowSellValue = (() => {
      if (lowOverride === null) return null;
      if (lowOverride && typeof lowOverride.raw === 'string' && lowOverride.raw.trim().length)
        return lowOverride.raw.trim();
      if (
        lowOverride &&
        typeof lowOverride.amount === 'number' &&
        Number.isFinite(lowOverride.amount)
      )
        return roundToTwo(lowOverride.amount).toFixed(2);
      return null;
    })();
  }
  if (highOverride !== undefined) {
    updatesPayload.highSellValue = (() => {
      if (highOverride === null) return null;
      if (highOverride && typeof highOverride.raw === 'string' && highOverride.raw.trim().length)
        return highOverride.raw.trim();
      if (
        highOverride &&
        typeof highOverride.amount === 'number' &&
        Number.isFinite(highOverride.amount)
      )
        return roundToTwo(highOverride.amount).toFixed(2);
      return null;
    })();
  }
  const hasLow = Object.prototype.hasOwnProperty.call(updatesPayload, 'lowSellValue');
  const hasHigh = Object.prototype.hasOwnProperty.call(updatesPayload, 'highSellValue');

  const lot = state.lots.find(item => item && item.id === id);

  log.debug('Found lot:', lot?.id, 'bidding:', lot?.bidding, 'bidAmount:', lot?.bidAmount);

  if (!hasLow && !hasHigh && !lot) return null;
  const body: any = {};
  if (hasLow) body.lowSellValue = updatesPayload.lowSellValue;
  if (hasHigh) body.highSellValue = updatesPayload.highSellValue;

  if (lot) {
    body.bidding = lot.bidding ?? false;
    if (typeof lot.bidAmount === 'number' && Number.isFinite(lot.bidAmount)) {
      body.bidAmount = lot.bidAmount;
    } else {
      body.bidAmount = null;
    }
    // Per-lot decision fields (manual max bid + buyer's premium override)
    body.userMaxBid =
      typeof lot.userMaxBid === 'number' && Number.isFinite(lot.userMaxBid) ? lot.userMaxBid : null;
    body.feeOverrides =
      lot.feeOverrides && Object.keys(lot.feeOverrides).length ? lot.feeOverrides : null;
    body.sellChannel =
      typeof lot.sellChannel === 'string' && lot.sellChannel ? lot.sellChannel : null;
  }

  log.debug('Sending body:', JSON.stringify(body));

  const response = await fetch(endpoint, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: JSON.stringify(body),
  });
  if (response.status === 401 && !retry) {
    (state as any).extAccessToken = null;
    return syncEstimateRemote(
      id,
      lowOverride,
      highOverride,
      true,
      state,
      ensureAccessToken,
      mapRemoteDocument
    );
  }
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || 'Failed to update estimates (' + response.status + ')');
  }
  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  if (contentType && contentType.indexOf('json') === -1)
    throw new Error('Unexpected response from API.');
  const data = await response.json();
  log.debug('Received response:', JSON.stringify(data));
  const mapped = mapRemoteDocument(data && data.document ? data.document : data);
  log.debug('Mapped lot - bidding:', mapped?.bidding, 'bidAmount:', mapped?.bidAmount);
  if (!mapped) throw new Error('Received empty response from API.');
  return mapped;
}
